import { StatCard } from '@/components/ui/Card';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useLabDashboard, useCriticalResults } from './hooks';
import { fmtRelative } from './ui';
import type { LabDashboard as LabDashboardData } from './types';

/**
 * The laboratory work board.
 *
 * Every number here is a server-side aggregate over this branch's rows (see
 * routes/laboratory/dashboard.js) — counting a fetched list in the browser
 * would give a different answer the moment that list is paginated, which it
 * is, and would make the branch filter a client-side suggestion rather than
 * a boundary.
 *
 * The tiles are ordered by the workflow, left to right, because that is how
 * the work actually moves and it lets someone see where the queue is backing
 * up without reading the labels.
 */

interface TileSpec {
  key: keyof LabDashboardData;
  label: string;
  /** Accent colour — reserved for the tiles that mean "act now". */
  accent?: string;
  onClick?: () => void;
}

export interface LabDashboardProps {
  /** Jumps to the tab and filter behind a tile — a dashboard nobody can drill into is a poster. */
  onNavigate?: (target: { tab: 'orders' | 'samples' | 'results' | 'outside'; filter?: Record<string, string> }) => void;
}

export default function LabDashboard({ onNavigate }: LabDashboardProps) {
  const { data, isLoading, error, refetch } = useLabDashboard();
  const criticals = useCriticalResults({ outstanding: true });

  if (isLoading) return <LoadingState label="Loading the laboratory board…" />;
  if (error) return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  if (!data) return null;

  const go = (tab: 'orders' | 'samples' | 'results' | 'outside', filter?: Record<string, string>) =>
    onNavigate ? () => onNavigate({ tab, filter }) : undefined;

  const workflow: TileSpec[] = [
    { key: 'today_orders',         label: "Today's orders",       onClick: go('orders') },
    { key: 'pending_collection',   label: 'Pending collection',   accent: 'var(--color-warn)', onClick: go('samples', { status: 'PENDING_COLLECTION' }) },
    { key: 'samples_awaiting_receipt', label: 'Awaiting receipt',  onClick: go('samples', { status: 'COLLECTED' }) },
    { key: 'result_pending',       label: 'Result pending',       onClick: go('results', { queue: 'pending' }) },
    { key: 'awaiting_verification', label: 'Verification pending', accent: 'var(--color-warn)', onClick: go('results', { queue: 'entered' }) },
    { key: 'report_pending',       label: 'Report pending',       onClick: go('orders', { status: 'VERIFIED' }) },
    { key: 'completed',            label: 'Completed',            onClick: go('orders', { status: 'REPORTED' }) },
  ];

  const attention: TileSpec[] = [
    { key: 'critical_unacknowledged', label: 'Critical — unacknowledged', accent: 'var(--color-danger)', onClick: go('results', { critical: '1' }) },
    { key: 'samples_rejected',        label: 'Rejected samples',          accent: 'var(--color-danger)', onClick: go('samples', { status: 'REJECTED' }) },
    { key: 'samples_recollection',    label: 'Recollection needed',       accent: 'var(--color-danger)', onClick: go('samples', { status: 'RECOLLECTION_REQUIRED' }) },
    { key: 'overdue',                 label: 'Past turnaround',           accent: 'var(--color-warn)',   onClick: go('results') },
    { key: 'urgent_open',             label: 'Urgent / STAT open',        accent: 'var(--color-warn)',   onClick: go('orders', { priority: 'urgent' }) },
  ];

  const outside: TileSpec[] = [
    { key: 'outsourced_to_send',              label: 'To send out',          onClick: go('outside', { queue: 'to_send' }) },
    { key: 'outsourced_awaiting_report',      label: 'Awaiting report',      onClick: go('outside', { queue: 'awaiting_report' }) },
    { key: 'outsourced_overdue',              label: 'Report overdue',       accent: 'var(--color-danger)', onClick: go('outside', { queue: 'overdue' }) },
    { key: 'outsourced_to_transcribe',        label: 'Report uploaded',      onClick: go('outside', { queue: 'to_transcribe' }) },
    { key: 'outsourced_pending_verification', label: 'Pending verification', accent: 'var(--color-warn)', onClick: go('outside', { queue: 'pending_verification' }) },
    { key: 'outsourced_to_finalize',          label: 'To finalize',          onClick: go('outside', { queue: 'to_finalize' }) },
  ];

  const tile = (t: TileSpec) => {
    const value = Number(data[t.key] ?? 0);
    const clickable = !!t.onClick;
    return (
      <StatCard
        key={String(t.key)}
        accentColor={value > 0 ? t.accent : undefined}
        className={clickable ? 'cursor-pointer transition-shadow hover:shadow-md' : undefined}
        onClick={t.onClick}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); t.onClick?.(); } } : undefined}
      >
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="mt-0.5 text-[12px] text-muted-foreground">{t.label}</div>
      </StatCard>
    );
  };

  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Workflow</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          {workflow.map(tile)}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">Outside / reference laboratory</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {outside.map(tile)}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold">Needs attention</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {attention.map(tile)}
        </div>
      </section>

      {/*
        Unacknowledged critical values are surfaced on the board itself, not
        only behind a tab. A critical result that nobody has read back is the
        one thing on this screen that is time-critical for a patient, and
        making someone navigate to find it defeats the escalation.
      */}
      {criticals.data && criticals.data.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold">
            Critical results awaiting acknowledgement
            <Badge variant="danger">{criticals.data.length}</Badge>
          </h3>
          <div className="rounded-xl border border-border bg-card divide-y divide-border">
            {criticals.data.slice(0, 5).map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-[13px]">
                <div>
                  <span className="font-semibold">{c.patient_name}</span>
                  <span className="text-muted-foreground"> · {c.uhid} · {c.order_number}</span>
                  <div className="text-muted-foreground">
                    {c.test_name} — {c.parameter_name}{' '}
                    <span className="font-semibold text-danger-ink">{c.value_display}</span>
                    {' · '}detected {fmtRelative(c.detected_at)}
                    {c.responsible_doctor_name ? ` · for ${c.responsible_doctor_name}` : ' · no responsible doctor recorded'}
                  </div>
                </div>
                <Badge variant={c.notification_status === 'failed' ? 'danger' : 'warn'}>
                  {c.notification_status === 'failed' ? 'Not delivered' : 'Notified'}
                </Badge>
              </div>
            ))}
          </div>
          {criticals.data.length > 5 && onNavigate && (
            <Button variant="ghost" size="sm" className="mt-2"
              onClick={() => onNavigate({ tab: 'results', filter: { critical: '1' } })}>
              View all {criticals.data.length}
            </Button>
          )}
        </section>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold">By department — last 30 days</h3>
        <div className="rounded-xl border border-border bg-card divide-y divide-border">
          {data.by_department.length === 0 && (
            <div className="px-4 py-3 text-[13px] text-muted-foreground">No tests ordered in the last 30 days.</div>
          )}
          {data.by_department.map((d) => (
            <div key={d.id ?? 'none'} className="flex items-center justify-between px-4 py-2 text-[13px]">
              <span>{d.name ?? 'Unassigned'}</span>
              <span className="text-muted-foreground tabular-nums">
                {d.open} open <span className="opacity-50">/</span> {d.total} total
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
